// What this browser remembers whoever is signed in: the language to show
// before sign-in, and the onboarding it has seen. A port, and an async one,
// because a native app keeps this in AsyncStorage.

import { detectLang, isLangId, type LangId } from "@aybuben/core/lang.ts";
import { isVersion } from "@aybuben/core/onboarding/version.ts";

export interface DevicePrefs {
	lang: LangId;
	onboarded: number;
}

export interface DeviceStore {
	load(): Promise<DevicePrefs>;
	save(p: DevicePrefs): Promise<void>;
}

export const DEVICE_KEY = "aybuben:device";

export const defaultDevice = (
	tag: string | null | undefined = globalThis.navigator?.language,
): DevicePrefs => ({ lang: detectLang(tag), onboarded: 0 });

/** Field by field, so one corrupt value does not cost the other. */
export function parseDevice(raw: string | null, fallback: DevicePrefs): DevicePrefs {
	if (!raw) return fallback;
	let x: unknown;
	try {
		x = JSON.parse(raw);
	} catch {
		return fallback;
	}
	if (typeof x !== "object" || x === null) return fallback;
	// Narrowed to a non-null object above; each field is re-checked before use.
	const { lang, onboarded } = x as { lang?: unknown; onboarded?: unknown };
	return {
		lang: typeof lang === "string" && isLangId(lang) ? lang : fallback.lang,
		onboarded: isVersion(onboarded) ? onboarded : fallback.onboarded,
	};
}

/** Degrades to "nothing remembered" when storage is blocked (private mode, site data off). */
export const localDeviceStore: DeviceStore = {
	async load() {
		let raw: string | null = null;
		try {
			raw = globalThis.localStorage?.getItem(DEVICE_KEY) ?? null;
		} catch {
			/* nothing remembered */
		}
		return parseDevice(raw, defaultDevice());
	},
	async save(p) {
		try {
			globalThis.localStorage?.setItem(DEVICE_KEY, JSON.stringify(p));
		} catch {
			/* remembered for this session only */
		}
	},
};
